import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { getCart } from '../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    const updateCartCount = () => {
      const cart = getCart();
      const total = cart.reduce((sum, item) => sum + item.quantity, 0);
      setCartCount(total);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('storage', updateCartCount);
    updateCartCount();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', updateCartCount);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg shadow-red-900/20' : 'bg-black/80'
      }`}
    >
      <div className="container mx-auto px-4">
        {/* Top bar with contact info */}
        <div className="hidden md:flex items-center justify-between py-2 border-b border-red-900/30">
          <div className="flex items-center gap-6 text-sm text-gray-300">
            <a href="tel:0767682464" className="flex items-center gap-2 hover:text-red-500 transition-colors">
              <Phone size={16} />
              <span>07 67 68 24 64</span>
            </a>
            <div className="flex items-center gap-2">
              <MapPin size={16} />
              <span>20 Boulevard Baudelaire, 13005 Marseille</span>
            </div>
          </div>
          <div className="text-sm text-red-500 font-semibold">
            Livraison Gratuite - Ouvert 7J/7
          </div>
        </div>

        {/* Main navigation */}
        <div className="flex items-center justify-between py-4">
          {/* Desktop Navigation - Left side */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-white hover:text-red-500 transition-colors font-medium"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              Accueil
            </Link>
            <Link to="/menu" className="text-white hover:text-red-500 transition-colors font-medium">
              Notre Menu
            </Link>
            <button
              onClick={() => scrollToSection('offres-section')}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold px-4 py-1.5 rounded-full hover:from-yellow-400 hover:to-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-yellow-500/30"
            >
              Offres
            </button>
            <button
              onClick={() => scrollToSection('notre-histoire-section')}
              className="text-white hover:text-red-500 transition-colors font-medium"
            >
              À Propos
            </button>
            <Link to="/contact" className="text-white hover:text-red-500 transition-colors font-medium">
              Contact
            </Link>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-4 ml-auto">
            <Link to="/cart">
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative text-white hover:text-red-500 hover:bg-red-500/10"
              >
                <ShoppingCart size={24} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
            
            <Link to="/menu">
              <Button 
                className="hidden md:flex bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold shadow-lg shadow-red-600/30"
              >
                Commander
              </Button>
            </Link>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-black/98 border-t border-red-900/30">
          <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
            <Link 
              to="/" 
              className="text-white hover:text-red-500 transition-colors font-medium py-2"
              onClick={(e) => {
                setIsMenuOpen(false);
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              Accueil
            </Link>
            <Link 
              to="/menu" 
              className="text-white hover:text-red-500 transition-colors font-medium py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Notre Menu
            </Link>
            <button 
              onClick={() => scrollToSection('offres-section')}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold px-4 py-2 rounded-full hover:from-yellow-400 hover:to-orange-400 transition-all w-fit"
            >
              Offres
            </button>
            <button 
              onClick={() => scrollToSection('notre-histoire-section')}
              className="text-white hover:text-red-500 transition-colors font-medium py-2 text-left"
            >
              À Propos
            </button>
            <Link 
              to="/contact" 
              className="text-white hover:text-red-500 transition-colors font-medium py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
            <div className="pt-4 border-t border-red-900/30">
              <Link to="/menu" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold">
                  Commander
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
